import { createSelector } from '@reduxjs/toolkit';
import type React from 'react';

import type { SliceStateFromReducer } from 'shared/types';
import type { TPreparedVitrinsOffer } from 'shared/types/TVitrinsService';

import type { vitrinsSlice } from 'entities/vitrins';
import { buildSummary } from 'entities/vitrins';

export type TVitrinsState = SliceStateFromReducer<typeof vitrinsSlice>;
export const selectPreparedVitrinsOffers = (state: TVitrinsState) => state.vitrinsOffers.preparedOffers;

type SummaryItem = {
  label: string;
  value: string;
  tooltip?: React.ReactNode;
};

export type BankItem = TPreparedVitrinsOffer & { summary: SummaryItem[] };

export type BankGroup = TPreparedVitrinsOffer & {
  otherOffers: BankItem[];
  summary: SummaryItem[];
};

export const selectUniqVitrinsOffers = createSelector(selectPreparedVitrinsOffers, (offers) => {
  const map: Record<string, BankGroup> = {};

  for (const offer of offers) {
    const org = offer.organization;
    const summary = buildSummary(offer);

    if (!map[org]) {
      map[org] = {
        ...offer,
        summary,
        otherOffers: [],
      };
    } else {
      map[org].otherOffers.push({
        ...offer,
        summary,
      });
    }
  }

  return Object.values(map);
});

export const selectMinRateFromVitrinsOffers = createSelector(selectUniqVitrinsOffers, (offers) => {
  const rates = offers
    .map((offer) => offer?.minRate)
    .filter((rate): rate is number => typeof rate === 'number' && Number.isFinite(rate) && rate > 0);

  if (!rates.length) {
    return null;
  }

  return Math.min(...rates);
});
